import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { DateTime } from 'luxon';

import { constants } from '../../../config';
import { AppLink, routeNaming } from '../../../routes';
import { ScreenStore } from '../../../stores/ScreenStore';
import { ProductImage } from '../../../common/product-image';
import { GlossaryEntry } from '../../../common/glossary-entry';
import { SecondaryButton } from '../../../common/secondary-button';
import { ProductHelper } from '../../../helpers/ProductHelper';
import styles from './ProductLine.css';
import { InfoModal } from '../../info-modal';
import { ConfigParametersStore } from '../../../stores/ConfigParametersStore';

const IProduct = PropTypes.shape({
  id: PropTypes.string.isRequired,
  coverImage: PropTypes.string,
  brand: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  financing: PropTypes.shape({
    priceString: PropTypes.string.isRequired,
  }).isRequired,
  serviceProduct: PropTypes.object,
});

const IPropTypes = {
  allowCoordination: PropTypes.bool,
  amount: PropTypes.number.isRequired,
  className: PropTypes.string,
  checkoutId: PropTypes.string,
  clickable: PropTypes.bool,
  installationOption: PropTypes.shape({
    date: PropTypes.instanceOf(DateTime).isRequired,
    amount: PropTypes.number.isRequired,
    startHourString: PropTypes.string.isRequired,
    endHourString: PropTypes.string.isRequired,
  }),
  item: PropTypes.shape({
    amount: PropTypes.number.isRequired,
    product: IProduct.isRequired,
    serviceAmount: PropTypes.number.isRequired,
  }).isRequired,
  hasService: PropTypes.bool,
};

const defaultProps = {
  allowCoordination: false,
  checkoutId: null,
  className: '',
  clickable: false,
  hasService: false,
  installationOption: null,
};

const renderProductImage = (cover, mobileVersion) => (
  <ProductImage
    src={cover}
    className={styles.productImage}
    size={mobileVersion ? 'sm' : 'md'}
  />
);

const renderProductName = name => (
  <h3 className={styles.name}>
    {name}
  </h3>
);

const formatDate = date => (
  `${date.toFormat('cccc d \'de\' MMMM')}`
);

const ProductLine = (props) => {
  const {
    item: { product, serviceAmount },
    hasService,
    amount,
    allowCoordination,
  } = props;
  const mobileVersion = ScreenStore.screenWidth <= constants.breakpoints.mobileThreshold;
  let installationTexts = null;
  if (hasService && serviceAmount && allowCoordination) {
    installationTexts = ProductHelper.findInstallationTextFor(product.serviceProduct);
  }
  const isArticleInPresale = ConfigParametersStore.isValueInFeatureFlagData('product_next_deliveries', product.id);
  return (
    <div className={[styles.container, props.className].join(' ')}>
      <div className={styles.productData}>
        {
          props.clickable ?
            <AppLink
              className={styles.imageContainer}
              routeName={routeNaming.PRODUCT_DETAIL}
              pathParams={{
                id: product.id,
              }}
            >
              {renderProductImage(product.coverImage, mobileVersion)}
            </AppLink> :
            renderProductImage(product.coverImage, mobileVersion)
        }
        <div className={styles.textContainer}>
          <p className={styles.brand}>
            {product.brand}
          </p>
          {
            props.clickable ?
              <AppLink
                routeName={routeNaming.PRODUCT_DETAIL}
                pathParams={{
                  id: product.id,
                }}
              >
                {renderProductName(product.name)}
              </AppLink> :
              renderProductName(product.name)
          }
          {
            hasService &&
            !!serviceAmount &&
            <InfoModal
              id="servicio-instalación-covid"
              type="component"
              showConfirm={false}
              textCancel="Cerrar"
              showModal={constants.activeCovid}
            >
              <p className={styles.installationText}>
                {isArticleInPresale ? product.textsService[1] : product.textsService[1]}
              </p>
            </InfoModal>
          }
          {
            !hasService &&
            !!product.serviceProduct &&
            <p className={styles.noInstallationText}>
              {isArticleInPresale ? product.textsService[2] : product.textsService[2]}
            </p>
          }
          <div className={styles.priceContainer}>
            ${ProductHelper.priceStringFor(product, hasService)} - Cantidad: {amount}
          </div>
          {
            !!props.installationOption &&
            <React.Fragment>
              <p className={styles.installationMessage}>
                Instalación: {formatDate(props.installationOption.date)}
                &nbsp;de {props.installationOption.startHourString}hs
                a {props.installationOption.endHourString}hs en la dirección de
                envío.
              </p>
              <p className={styles.installationWarning}>
                Para modificar la fecha o dirección de la instalación de este equipo
                &nbsp;, comunicate al teléfono {constants.contactPhoneNumber.text}.
              </p>
            </React.Fragment>
          }
          {
            hasService &&
            product.addedService === 0 &&
            !!serviceAmount &&
            allowCoordination &&
            <React.Fragment>
              <p className={styles.installationNotSet}>
                Instalación sin coordinar
              </p>
              {
                !!installationTexts &&
                !!installationTexts.length &&
                installationTexts.map(glossaryEntry => (
                  <p
                    key={glossaryEntry.entries[0].text}
                    className={styles.installationDataText}
                  >
                    <GlossaryEntry entry={glossaryEntry} />
                  </p>
                ))
              }
              <AppLink
                routeName={routeNaming.COORDINATION}
                pathParams={{
                  checkoutId: props.checkoutId,
                  productId: product.id,
                }}
              >
                <SecondaryButton className={styles.coordinationButton}>
                  Coordinar
                </SecondaryButton>
              </AppLink>
            </React.Fragment>
          }
        </div>
      </div>
    </div>
  );
};

ProductLine.propTypes = IPropTypes;
ProductLine.defaultProps = defaultProps;

const ObservedProductLine = observer(ProductLine);

export { ObservedProductLine as ProductLine };



// WEBPACK FOOTER //
// ./src/common/checkout-summary/product-line/ProductLine.jsx